import { useForm } from 'react-hook-form';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useSuspenseHostsCount } from '#api/hosts.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { CodeBlock } from '#components/atoms/code/index.ts';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import { useDeleteAccountMutation } from './DeleteAccountModal.queries.ts';

type Props = {
  onClose: () => void;
  orgId: string;
  orgName: string;
  email: string;
  // If true, the user is returning from step-up auth, prefill the input
  prefilled?: boolean;
};

export function DeleteAccountModal({ orgName, orgId, email, onClose, prefilled }: Props) {
  const deletionText = `DELETE ${orgName}`;

  const hostCount = useSuspenseHostsCount().data;
  const mutation = useDeleteAccountMutation(email);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({ defaultValues: { confirmation: prefilled ? deletionText : '' } });

  const onSubmit = handleSubmit(async () => {
    try {
      await mutation.mutateAsync(orgId);
    } catch (error) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ message }) => {
          setError('confirmation', { message });
          return true;
        });
      } else {
        setError('confirmation', { message: 'An unexpected error occurred' });
        trackError(error);
      }
    }
  });

  const formattedHostCount = () => {
    if (hostCount && hostCount > 1) return `${hostCount} hosts`;
    return 'hosts';
  };

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalHeader>Delete organization</ModalHeader>
      <ModalBody>
        <p className="p">
          Deleting your entire account is immediate and cannot be undone. All {formattedHostCount()}, roles, tags,
          users, etc. will be lost. Are you sure you want to proceed with account deletion?
        </p>
        <p className="p">Confirm by typing the following below:</p>
        <CodeBlock className="mb-24">{deletionText}</CodeBlock>

        <form action="POST" id="confirm-deletion-form" onSubmit={onSubmit}>
          <Field label="Confirm permanent deletion">
            <Input
              className="width-100"
              {...register('confirmation', {
                validate: (data) => {
                  return data === deletionText || 'Please type the confirmation text exactly';
                },
              })}
            />
            {errors.confirmation?.message && <ErrorMessage>{errors.confirmation.message}</ErrorMessage>}
          </Field>
        </form>
      </ModalBody>
      <ModalFooter>
        <ConfirmButtonGroup
          variant="right"
          primaryButton={
            <Button type="submit" form="confirm-deletion-form" variant="danger">
              Delete
            </Button>
          }
          secondaryButton={
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
          }
        />
      </ModalFooter>
    </Modal>
  );
}
